/**
 * API Endpoints for EASI 2.0
 *
 * Centralized endpoint definitions and typed API functions.
 *
 * HIPAA Requirements:
 * - All requests authenticated via Cognito JWT
 * - PHI transmitted over HTTPS only
 * - Audit logging on backend for all PHI access
 */

import { get, post, put, del, patch, uploadFile, type ApiResponse } from './client';
import { config } from '../config/environment';

/** Recursively convert snake_case keys to camelCase. */
function snakeToCamel<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(snakeToCamel) as T;
  if (obj && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [
        k.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase()),
        snakeToCamel(v),
      ]),
    ) as T;
  }
  return obj;
}

// ============================================================================
// Types
// ============================================================================

/**
 * Patient information
 */
export interface Patient {
  patientId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  ethnicity?: string;
  state?: string;
  primaryLanguage: string;
  educationalPlacement?: string;
  healthNotes?: string;
  clinicianId: string;
  createdAt: string;
  updatedAt: string;
  preferredTerm?: 'patient' | 'student' | 'child' | 'client';
  languageProfile?: {
    hearsOtherLanguages: string;
    additionalLanguages: string[];
    languageExposure: Record<string, number>;
    bilingualType: string;
    dialect: string;
    interpreterUsed: boolean;
  };
  educationalCultural?: {
    educationalPlacement: string;
    hasIep: string;
    eyeContactCulturallyExpected: string;
    narrativeTradition: string;
  };
  medicalDevelopmental?: {
    birthTiming: string;
    hearing: string;
    earInfections: string;
    diagnosedConditions: string[];
    previousTherapy: string;
    therapyDuration: string;
    communicationPartners: string[];
  };
  clinicalContext?: {
    clinicianSpeaksHomeLanguage: string;
    clinicianSpeaksFamilyDialect: string;
  };
}

/**
 * Session/assessment record
 */
export interface Session {
  sessionId: string;
  patientId: string;
  clinicianId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'created';
  audioFileKey?: string;
  transcript?: string;
  targetPhonemes?: string[];
  createdAt: string;
  completedAt?: string;
}

/**
 * Transcription result
 */
export interface LdcSegment {
  index: number;
  ldcChanged: boolean;
  ldcConfidence: number;
  ldcReasoning: string;
  originalSpeakerLabel: string;
}

export interface LdcFlag {
  index: number;
  reason: string;
}

export interface TranscriptionResult {
  sessionId: string;
  status?: string;
  errorMessage?: string;
  transcript: string;
  confidence: number;
  duration: number;
  wordTimings: Array<{
    word: string;
    start: number;
    end: number;
    confidence: number;
  }>;
  segments?: Array<{
    speaker: string;
    text: string;
    start_time?: number;
    end_time?: number;
    confidence?: number;
    needs_review?: boolean;
    has_overlap?: boolean;
    timing_prediction?: string;
    ldc_confidence?: number;
  }>;
  ldcSegments?: LdcSegment[];
  ldcFlags?: LdcFlag[];
  second_look_status?: string;
  progress?: number;
}

/**
 * Phoneme analysis result
 */
export interface PhonemeAnalysis {
  sessionId: string;
  targetPhonemes: string[];
  producedPhonemes: string[];
  alignment: Array<{
    target: string;
    produced: string;
    match: boolean;
    position: number;
  }>;
  accuracy: number;
  substitutions: Array<{
    target: string;
    produced: string;
    count: number;
  }>;
  deletions: string[];
  insertions: string[];
}

/**
 * Assessment report
 */
export interface AssessmentReport {
  reportId: string;
  sessionId: string;
  patientId: string;
  generatedAt: string;
  summary: string;
  phonemeAnalysis: PhonemeAnalysis;
  recommendations: string[];
  clinicianNotes?: string;
  status?: string;
  reportType?: string;
  generatedReport?: Record<string, string>;
  complianceEvidence?: Record<string, any>;
  eligibilityRoute?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string;
  downloadUrl?: string;
  documentSummaries?: Array<Record<string, any>>;
  generationStage?: string;
  generationMessage?: string;
  error?: string;
  disclaimers?: string[];
  content?: Record<string, any>;
  generation_duration_sec?: number;
  generationDurationSec?: number;
}

/**
 * Upload URL response for direct S3 upload
 */
export interface UploadUrlResponse {
  uploadUrl: string;
  fileKey: string;
  expiresAt: string;
}

/**
 * Upload URL response for direct document upload
 */
export interface DocumentUploadUrlResponse {
  uploadUrl: string;
  documentId: string;
  s3Key: string;
  expiresIn: number;
}

/**
 * Ingestion response after a document is processed
 */
export interface DocumentIngestResponse {
  documentId: string;
  patientId: string;
  status: string;
  summary?: string;
  documentType?: string;
  metadata?: Record<string, unknown>;
  updatedAt?: string;
}

/**
 * Patient document summary row
 */
export interface PatientDocument {
  documentId: string;
  fileName: string;
  uploadedAt: string;
  updatedAt?: string;
  type?: string;
  status?: string;
  summary?: string;
}

/**
 * Pagination parameters
 */
export interface PaginationParams {
  limit?: number;
  nextToken?: string;
}

/**
 * Paginated response
 */
export interface PaginatedResponse<T> {
  items: T[];
  nextToken?: string;
  count: number;
}

// ============================================================================
// CIM (Contextual Interpretation Model) Types
// ============================================================================

/**
 * Language entry for linguistic profile
 */
export interface LanguageEntry {
  language: string;
  isPrimary: boolean;
  exposurePercentage: number;
  ageFirstExposureMonths: number;
}

/**
 * Linguistic profile for CIM intake
 */
export interface LinguisticProfile {
  languages: LanguageEntry[];
  bilingualType: 'monolingual' | 'simultaneous' | 'sequential_early' | 'sequential_late';
  dialect: string;
  transferLanguage?: string;
  interpreterUsed?: boolean;
}

/**
 * Developmental and health profile for CIM intake
 */
export interface DevelopmentalHealthProfile {
  gestationalAgeWeeks?: number;
  hearingStatus: string;
  otitisMediaHistory?: boolean;
  otitisMediaEpisodes?: number;
  diagnosedConditions: string[];
  previousSpeechTherapy: boolean;
  therapyDurationMonths?: number;
  educationalPlacement?: string;
}

/**
 * Communicative environment profile for CIM intake
 */
export interface CommunicativeEnvironmentProfile {
  primaryCommunicationPartners: string[];
  communicationStyle: string;
  eyeContactCulturallyExpected?: boolean;
  directResponseExpected?: boolean;
  narrativeTradition?: string;
  aacUse?: boolean;
  aacSystem?: string;
}

/**
 * Assessment context for CIM intake
 */
export interface AssessmentContext {
  setting: string;
  rapport: string;
  state: string;
  examinerSpeaksChildLanguage: boolean;
  examinerSpeaksChildDialect: boolean;
  sessionDurationMinutes?: number;
  cooperationLevel?: string;
  distractions?: string;
  fatigue?: string;
}

/**
 * Full CIM contextual intake data
 */
export interface ContextualIntakeData {
  patientId: string;
  sessionId: string;
  linguisticProfile: LinguisticProfile;
  developmentalProfile: DevelopmentalHealthProfile;
  communicativeEnvironment: CommunicativeEnvironmentProfile;
  assessmentContext: AssessmentContext;
}

/**
 * CIM flag from the backend
 */
export interface CimFlag {
  level: 'CONTEXT_CRITICAL' | 'CONTEXT_WARNING' | 'CONTEXT_INFO';
  category: string;
  title: string;
  detail: string;
  reportText: string;
  citations: string[];
}

/**
 * Request to generate CIM flags
 */
export interface FlagGenerationRequest {
  patientId: string;
  sessionId: string;
  scores: {
    pcc?: number;
    mlu?: number;
    ipsyn?: number;
    ndw?: number;
    dss?: number;
    vot?: { voicedMs: number; voicelessMs: number; contrastPair: string };
    intelligibility?: number;
    sldCount?: number;
    totalSyllables?: number;
    percentStutteredSyllables?: number;
  };
}

/**
 * Response from flag generation
 */
export interface FlagGenerationResponse {
  flags: CimFlag[];
  missingContextWarnings: string[];
  flagCount: {
    critical: number;
    warning: number;
    info: number;
  };
}

// ============================================================================
// Patient Endpoints
// ============================================================================

export const patients = {
  /**
   * List all patients for the authenticated clinician
   */
  list: (params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<Patient>>> =>
    get('/patients', { params }),

  /**
   * Get a single patient by ID
   */
  get: (patientId: string): Promise<ApiResponse<Patient>> =>
    get(`/patients/${patientId}`),

  /**
   * Create a new patient
   */
  create: (data: Omit<Patient, 'patientId' | 'clinicianId' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<Patient>> =>
    post('/patients', data),

  /**
   * Update patient information
   */
  update: (patientId: string, data: Partial<Patient>): Promise<ApiResponse<Patient>> =>
    put(`/patients/${patientId}`, data),

  /**
   * Delete a patient (soft delete)
   */
  delete: (patientId: string): Promise<ApiResponse<void>> =>
    del(`/patients/${patientId}`),

  /**
   * Get patient's sessions
   */
  getSessions: (patientId: string, params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<Session>>> =>
    get(`/patients/${patientId}/sessions`, { params }),

  /**
   * Parse natural-language dictation into structured patient fields
   */
  parseDictation: (transcript: string): Promise<ApiResponse<Record<string, any>>> =>
    post('/patients/parse-dictation', { transcript }),
};

// ============================================================================
// Session Endpoints
// ============================================================================

export const sessions = {
  /**
   * List sessions for authenticated clinician
   */
  list: (params?: PaginationParams & { status?: Session['status'] }): Promise<ApiResponse<PaginatedResponse<Session>>> =>
    get('/sessions', { params }),

  /**
   * Get a single session
   */
  get: (sessionId: string): Promise<ApiResponse<Session>> =>
    get(`/sessions/${sessionId}`),

  /**
   * Create a new assessment session
   */
  create: (data: { patientId: string; targetPhonemes?: string[] }): Promise<ApiResponse<Session>> =>
    post('/sessions', data),

  /**
   * Update session
   */
  update: (sessionId: string, data: Partial<Session>): Promise<ApiResponse<Session>> =>
    put(`/sessions/${sessionId}`, data),

  /**
   * Cancel a session
   */
  cancel: (sessionId: string): Promise<ApiResponse<Session>> =>
    post(`/sessions/${sessionId}/cancel`),

  /**
   * Soft-delete a session
   */
  delete: (sessionId: string): Promise<ApiResponse<void>> =>
    post(`/sessions/${sessionId}/delete`),

  /**
   * Get presigned URL for audio upload
   */
  getUploadUrl: (sessionId: string, contentType: string): Promise<ApiResponse<UploadUrlResponse>> =>
    post(`/sessions/${sessionId}/upload-url`, { contentType }),

  /**
   * Confirm audio upload completed
   */
  confirmUpload: (sessionId: string, fileKey: string): Promise<ApiResponse<Session>> =>
    post(`/sessions/${sessionId}/confirm-upload`, { fileKey }),

  /**
   * Get presigned URL for audio playback
   */
  getAudioUrl: (sessionId: string): Promise<ApiResponse<{ audioUrl: string; expiresIn: number }>> =>
    get(`/sessions/${sessionId}/audio-url`),

  /**
   * Re-run diarization using clinician corrections as context
   */
  rerunDiarization: (sessionId: string, corrections: Array<{ index: number; corrected_speaker: string }>): Promise<ApiResponse<{ corrections_applied: number; segments: any[] }>> =>
    post(`/sessions/${sessionId}/rerun-diarization`, { corrections }),

  // -- Real-time recording endpoints --

  /**
   * Get batch of presigned URLs for recording chunk uploads
   */
  getChunkUploadUrls: (
    sessionId: string,
    startIndex: number,
    count: number,
    contentType: string
  ): Promise<ApiResponse<{ urls: Array<{ index: number; uploadUrl: string; s3Key: string }> }>> =>
    post(`/sessions/${sessionId}/chunk-upload-urls`, { startIndex, count, contentType }),

  /**
   * Confirm a recording chunk was uploaded to S3
   */
  confirmChunk: (
    sessionId: string,
    chunkIndex: number,
    s3Key: string
  ): Promise<ApiResponse<{ chunkIndex: number; confirmedTotal: number }>> =>
    post(`/sessions/${sessionId}/confirm-chunk`, { chunkIndex, s3Key }),

  /**
   * Finalize recording and trigger server-side assembly
   */
  finalizeRecording: (
    sessionId: string,
    totalChunks: number,
    contentType: string
  ): Promise<ApiResponse<{ taskId: string; statusUrl: string }>> =>
    post(`/sessions/${sessionId}/finalize-recording`, { totalChunks, contentType }),
};

// ============================================================================
// Transcription Endpoints
// ============================================================================

export const transcription = {
  /**
   * Start transcription for a session
   */
  start: (sessionId: string, speakerCount?: number): Promise<ApiResponse<{ taskId: string }>> =>
    post(`/transcription/${sessionId}/start`, speakerCount ? { speaker_count: speakerCount } : undefined),

  /**
   * Get transcription status and result
   */
  getResult: (sessionId: string): Promise<ApiResponse<TranscriptionResult>> =>
    get(`/transcription/${sessionId}`),

  /**
   * Submit a manually entered transcript (no audio)
   */
  submitManual: (
    sessionId: string,
    segments: Array<{ speaker: 'CLINICIAN' | 'CHILD'; text: string }>
  ): Promise<ApiResponse<{ sessionId: string; transcriptId: string; segmentCount: number }>> =>
    post(`/transcription/${sessionId}/manual`, { segments }),

  /**
   * Update/correct transcription
   */
  update: (
    sessionId: string,
    data: {
      transcript: string;
      verificationStatus?: "VERIFIED" | "CORRECTED" | "REJECTED" | "PENDING";
      correctionCount?: number;
      verifierCredentials?: string;
      affirmedSegments?: Array<{
        speaker: string;
        text: string;
        startTime?: number;
        endTime?: number;
        isNew?: boolean;
        isEdited?: boolean;
        textApproved?: boolean;
        ipa?: string;
        ipaApproved?: boolean;
        ipaEdited?: boolean;
        ipaSource?: string;
      }>;
    }
  ): Promise<ApiResponse<TranscriptionResult>> =>
    put(`/transcription/${sessionId}`, data),
};

// ============================================================================
// Verification Endpoints
// ============================================================================

export const verification = {
  /**
   * Curate transcript with Second Look after clinician confirms speaker roles.
   * Synchronous — waits for Bedrock inference (15-30s).
   */
  curate: (
    sessionId: string,
    data: {
      speaker_mapping: Record<string, { role: string; pipelineRole: string; displayName: string; customName?: string }>;
      speaker_count?: number;
    }
  ): Promise<ApiResponse<{
    session_id: string;
    transcript: string;
    segments: Array<{ speaker: string; speaker_label: string; text: string; [key: string]: any }>;
    curated: boolean;
    second_look_status: string;
    corrections_made: number;
  }>> =>
    post(`/verification/${sessionId}/curate`, data),
};

// ============================================================================
// Phoneme Analysis Endpoints
// ============================================================================

export const phonemeAnalysis = {
  /**
   * Start phoneme analysis for a session
   */
  start: (sessionId: string, data?: { targetPhonemes?: string[]; client_age_months?: number }): Promise<ApiResponse<{ taskId: string }>> =>
    post(`/analysis/${sessionId}/start`, data),

  /**
   * Get phoneme analysis result
   */
  getResult: (sessionId: string): Promise<ApiResponse<PhonemeAnalysis>> =>
    get(`/analysis/${sessionId}`),

  /**
   * Get detailed alignment visualization data
   */
  getAlignment: (sessionId: string): Promise<ApiResponse<PhonemeAnalysis['alignment']>> =>
    get(`/analysis/${sessionId}/alignment`),

  /**
   * Rescore session with normative scoring (no audio reprocessing)
   */
  rescore: (sessionId: string, data?: { client_age_months?: number }): Promise<ApiResponse<{ scoring_data: Record<string, any>; session_id: string }>> =>
    post(`/analysis/${sessionId}/rescore`, data),

  /**
   * Look up target IPA for words (used by Speech Sound Annotation tool)
   */
  wordIpa: (words: string[]): Promise<ApiResponse<{ results: Record<string, string[]> }>> =>
    post('/analysis/word-ipa', { words }),
};

// ============================================================================
// Report Endpoints
// ============================================================================

export const reports = {
  /**
   * Generate assessment report
   */
  generate: (
    data: {
      sessionId?: string;
      patientId?: string;
      documentIds?: string[];
      reportType?: string;
    }
  ): Promise<ApiResponse<AssessmentReport>> =>
    post(`/reports/generate`, {
      ...(data.sessionId ? { sessionId: data.sessionId } : {}),
      ...(data.patientId ? { patientId: data.patientId } : {}),
      ...(data.documentIds ? { documentIds: data.documentIds } : {}),
      ...(data.reportType ? { reportType: data.reportType } : {}),
    }),

  /**
   * Get report by ID
   */
  get: (reportId: string): Promise<ApiResponse<AssessmentReport>> =>
    get(`/reports/${reportId}`),

  /**
   * List reports for a patient
   */
  listByPatient: (patientId: string, params?: PaginationParams): Promise<ApiResponse<PaginatedResponse<AssessmentReport>>> =>
    get(`/patients/${patientId}/reports`, { params }),

  /**
   * Add clinician notes to report
   */
  addNotes: (reportId: string, notes: string): Promise<ApiResponse<AssessmentReport>> =>
    put(`/reports/${reportId}/notes`, { notes }),

  /**
   * Export report as PDF
   */
  exportPdf: (reportId: string): Promise<ApiResponse<{ downloadUrl?: string; filename?: string; format?: string; note?: string; file_data?: string; content_type?: string }>> =>
    get(`/reports/${reportId}/export/pdf`),

  /**
   * Soft-delete a report
   */
  delete: (reportId: string): Promise<ApiResponse<{ reportId: string; deleted: boolean }>> =>
    post(`/reports/${reportId}/delete`, {}),

  /**
   * Create a new template-based report (with optional roll-forward from prior)
   */
  createNew: (data: NewReportRequest): Promise<ApiResponse<NewReportResponse>> =>
    post(`/patients/${data.patientId}/reports/new`, data),

  /**
   * Save draft field values
   */
  saveDraft: (
    reportId: string,
    data: { fieldValues?: Record<string, string | boolean>; goalDispositions?: Record<string, { status: string; notes?: string }> }
  ): Promise<ApiResponse<{ reportId: string; status: string; updatedAt: string | null }>> =>
    put(`/reports/${reportId}`, data),

  /**
   * Finalize a report (locks it)
   */
  finalize: (reportId: string): Promise<ApiResponse<{ reportId: string; status: string; finalizedAt: string; auditHash: string }>> =>
    post(`/reports/${reportId}/finalize`, {}),
};

// ============================================================================
// Document Endpoints
// ============================================================================

export const documents = {
  /**
   * Get presigned upload URL for a document.
   */
  getUploadUrl: (data: {
    patientId: string;
    filename: string;
    contentType: string;
    documentType?: string;
  }): Promise<ApiResponse<DocumentUploadUrlResponse>> =>
    post('/documents/upload', {
      patientId: data.patientId,
      filename: data.filename,
      contentType: data.contentType,
      ...(data.documentType ? { documentType: data.documentType } : {}),
    }),

  /**
   * Trigger ingestion for an uploaded document.
   */
  ingest: (data: {
    patientId: string;
    documentId: string;
    documentType?: string;
    source?: string;
    documentDate?: string;
  }): Promise<ApiResponse<DocumentIngestResponse>> =>
    post('/documents/ingest', {
      patientId: data.patientId,
      documentId: data.documentId,
      ...(data.documentType ? { documentType: data.documentType } : {}),
      ...(data.source ? { source: data.source } : {}),
      ...(data.documentDate ? { documentDate: data.documentDate } : {}),
    }),

  /**
   * List all documents for a patient.
   */
  listByPatient: (patientId: string): Promise<ApiResponse<{
    patientId: string;
    items: PatientDocument[];
    count: number;
  }>> =>
    get(`/patients/${patientId}/documents`),

  /**
   * Get document details (status, summary, metadata).
   */
  getDocument: (patientId: string, documentId: string): Promise<ApiResponse<any>> =>
    get(`/documents/${patientId}/${documentId}`),

  /**
   * Get presigned download URL for a document.
   */
  getDownloadUrl: (patientId: string, documentId: string): Promise<ApiResponse<{ downloadUrl: string; expiresIn: number }>> =>
    get(`/documents/${patientId}/${documentId}/download-url`),

  /**
   * Soft-delete a document.
   */
  remove: (patientId: string, documentId: string): Promise<ApiResponse<{ documentId: string; status: string }>> =>
    del(`/documents/${patientId}/${documentId}`),

  /**
   * Check if a document's content matches the expected patient name.
   * Uses AI to scan document text for names and compares.
   */
  checkName: (data: {
    documentId: string;
    patientId: string;
    expectedName: string;
  }): Promise<ApiResponse<{
    nameMatch: boolean;
    foundNames: string[];
    confidence: number;
  }>> =>
    post('/documents/check-name', data),

  /**
   * Check if a document appears to be a copyrighted test protocol.
   * Uses AI to scan for publisher logos, "Do Not Reproduce" notices, etc.
   */
  checkCopyright: (data: {
    documentId: string;
    patientId: string;
  }): Promise<ApiResponse<{
    isCopyrighted: boolean;
    confidence: number;
    detectedTest?: string;
    reason?: string;
  }>> =>
    post('/documents/check-copyright', data),
};

// ============================================================================
// Context (CIM) Endpoints
// ============================================================================

export const context = {
  /**
   * Save contextual intake profile for a patient/session
   */
  saveIntake: (data: ContextualIntakeData): Promise<ApiResponse<{ profileId: string }>> =>
    post('/context/intake', data),

  /**
   * Get previously saved contextual intake profile
   */
  getIntake: (patientId: string): Promise<ApiResponse<ContextualIntakeData>> =>
    get(`/context/intake/${patientId}`),

  /**
   * Generate CIM flags from scores + contextual profile
   */
  generateFlags: (data: FlagGenerationRequest): Promise<ApiResponse<FlagGenerationResponse>> =>
    post('/context/flags', data),
};

// ============================================================================
// MySLP Endpoints
// ============================================================================

/**
 * MySLP consistency check issue
 */
export interface MySLPIssue {
  check: string;
  message: string;
  field: string;
  suggestion: string;
}

/**
 * MySLP consistency check result
 */
// MySLP Score Review types
export interface ScoreInterpretation {
  narrative: string;
  confidence: 'solid' | 'rough_estimate' | 'check_this';
  confidence_note: string;
  age_context: string;
  citation?: string;
}

export interface ScoreReviewResult {
  score_interpretations: Record<string, ScoreInterpretation>;
  profile_coherence: {
    makes_sense: boolean;
    plain_language_flag?: string;
    possible_reasons?: string[];
  };
  clinical_summary: string;
  report_guidance: string;
  impossible_combinations?: string[];
  reviewed_at?: string;
  model?: string;
  raw_scores_hash?: string;
}

// MySLP IPA Targets types
export interface IpaTargetsResult {
  utterances: Array<{
    line: number;
    words: Array<{
      word: string;
      ipa: string;
      phonemes: string[];
      ambiguous?: boolean;
    }>;
  }>;
  generated_at?: string;
  model?: string;
}

export interface TranscriptAnalysisResult {
  pragmatic_functions?: {
    observed?: Array<{ function: string; examples?: string[]; frequency?: string }>;
    not_observed?: string[];
    range_assessment?: string;
    citation?: string;
  };
  turn_taking?: {
    initiations?: number;
    responses?: number;
    contingent_responses?: number;
    non_contingent?: number;
    ratio_description?: string;
    age_context?: string;
    citation?: string;
  };
  topic_maintenance?: {
    maintains_topic?: boolean;
    topic_shifts?: string;
    exchanges_per_topic?: string;
    age_context?: string;
    citation?: string;
  };
  narrative_skills?: {
    narrative_level?: string;
    story_grammar_elements?: string[];
    temporal_sequencing?: string;
    causal_connections?: string;
    character_reference?: string;
    age_context?: string;
    citation?: string;
    note?: string;
  };
  social_communication?: {
    joint_attention?: string;
    perspective_taking?: string;
    indirect_language?: string;
    overall_assessment?: string;
    age_context?: string;
    citation?: string;
  };
  echolalia_screen?: {
    immediate_echo_count?: number;
    delayed_echo_possible?: number;
    generative_utterances?: number;
    percentage_generative?: string;
    clinical_note?: string;
    citation?: string;
  };
  discourse_cohesion?: {
    pronoun_use?: string;
    conjunction_use?: string;
    reference_clarity?: string;
    citation?: string;
  };
  utterance_functions?: Array<{
    utterance: string;
    speaker: 'child' | 'adult';
    function?: string;
    adult_function?: string;
  }>;
  function_summary?: {
    child_function_counts?: Record<string, number>;
    adult_function_counts?: Record<string, number>;
    child_function_diversity?: string;
    interpretation?: string;
  };
  clinical_summary?: string;
  report_section?: string;
  analyzed_at?: string;
  model?: string;
}

export interface MySLPCheckResult {
  check_id: string;
  status: 'PASSED' | 'PASSED_WITH_WARNINGS' | 'FAILED';
  summary: {
    issues_count: number;
    warnings_count: number;
    checks_passed: number;
    checks_total: number;
  };
  issues: MySLPIssue[];
  warnings: MySLPIssue[];
  results_url: string;
}

// MySLP calls routed through API Gateway with Cognito auth (no direct Lambda URLs)

/**
 * MySLP chat message in conversation history
 */
export interface MySLPChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * MySLP chat response from Lambda
 */
export interface MySLPChatResponse {
  response: string;
  imageUrl?: string;
  metadata?: {
    queryType?: string;
    complexity?: string;
    tier?: number;
    tierLabel?: string;
    model?: string;
    tokenCost?: number;
    tokensDeducted?: number;
    tokensRemaining?: number;
    degraded?: boolean;
    tokensUsed?: { input?: number; output?: number };
    knowledgeSourcesUsed?: number;
    subscriptionExpired?: boolean;
    tierBlocked?: boolean;
    tokensRequired?: number;
    tokensAvailable?: number;
    canDowngrade?: boolean;
    core_board?: CoreBoardData;
    contextWindow?: {
      estimatedTokens: number;
      maxTokens: number;
      percentUsed: number;
      warningLevel: 'none' | 'caution' | 'warning' | 'critical';
      tier?: number;
    };
    contextLimitReached?: boolean;
    timeoutRecovery?: boolean;
  };
}

/** Structured result from the generate_core_board MySLP tool */
export interface CoreBoardCell {
  word: string;
  function: string;
  color: string;
  position: number;
  image_prompt: string;
  teaching_tip: string;
}

export interface CoreBoardData {
  board_name: string;
  grid_size: number;
  communication_level: string;
  cells: CoreBoardCell[];
  parent_summary: string;
  polly_voice: string;
  matched_goal_ids: string[];
  interests_used: string[];
  functions_included: string[];
}

export const myslp = {
  /**
   * Send a chat message to MySLP.
   * Uses Lambda Function URL (no 29s timeout) when configured, falls back to API Gateway.
   */
  chat: async (
    message: string,
    conversationHistory: MySLPChatMessage[] = [],
    clinicianId?: string,
    context?: { screen?: string; patientId?: string; patientName?: string; sessionId?: string },
    imageData?: { image: string; contentType: string },
    attachments?: Array<{ s3Key: string; fileName: string; fileType: string; fileSize: number }>,
    retryAfterTimeout?: boolean,
  ): Promise<MySLPChatResponse> => {
    const payload = {
      message,
      conversationHistory,
      clinicianId,
      context,
      ...(imageData ? { image: imageData.image, imageContentType: imageData.contentType } : {}),
      ...(attachments?.length ? { attachments } : {}),
      ...(retryAfterTimeout ? { retryAfterTimeout: true } : {}),
    };

    // Route through CloudFront /api/myslp/* → Lambda Function URL (60s timeout,
    // vs API Gateway's 29s).  Same origin as the frontend — no ad-blocker issues.
    if (config.myslpFunctionUrl) {
      const { getIdToken } = await import('../auth/cognito-client');
      const token = await getIdToken();
      const res = await fetch(`${window.location.origin}/api/myslp/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(900000),
      });
      if (!res.ok) {
        const errorBody = await res.text().catch(() => '');
        throw new Error(errorBody || `MySLP request failed (${res.status})`);
      }
      const json = await res.json();
      return (json.data ?? json) as MySLPChatResponse;
    }

    // Fallback: API Gateway (subject to 29s timeout)
    const resp = await post<{ response: string; imageUrl?: string; metadata?: MySLPChatResponse['metadata'] }>(
      '/myslp/chat',
      payload,
    );
    return resp.data as MySLPChatResponse;
  },

  /**
   * Get presigned URL for document upload to MySLP.
   */
  getDocUploadUrl: async (
    fileName: string,
    fileType: string,
    fileSize: number,
  ): Promise<{ uploadUrl: string; s3Key: string }> => {
    const resp = await post<{ uploadUrl: string; s3Key: string }>(
      '/myslp/upload-url',
      { fileName, fileType, fileSize },
    );
    return resp.data as { uploadUrl: string; s3Key: string };
  },

  /**
   * Run Second Look consistency check via authenticated API Gateway
   */
  secondLook: async (data: {
    action: 'consistency_check';
    patient_id: string;
    analysis_job_id?: string;
    report_id?: string;
    eligibility_determination?: {
      eligible: boolean;
      category: string;
      severity: string;
    };
  }): Promise<MySLPCheckResult> => {
    const { action: _, ...checkData } = data;
    const resp = await post<MySLPCheckResult>('/myslp/check', checkData);
    return resp.data as MySLPCheckResult;
  },

  /**
   * Report Interview — conversational data gathering for report wizard
   */
  reportInterview: async (
    message: string,
    conversationHistory: Array<{ role: string; content: string }>,
    patientId?: string,
    documentSummaries?: Array<{ fileName: string; summary: string }>,
    discipline?: string,
  ): Promise<{ response: string }> => {
    const resp = await post<{ response: string }>('/myslp/report-interview', {
      message,
      conversationHistory,
      patientId,
      documentSummaries,
      discipline,
    });
    return resp.data as { response: string };
  },

  /**
   * MySLP Clinical Score Review — reasoning layer over raw scores
   */
  scoreReview: async (data: {
    session_id: string;
    child_age_months: number;
    child_name: string;
    verification_type: 'COMPREHENSIVE' | 'LANGUAGE_ONLY';
    sample_metadata: {
      total_utterances?: number;
      child_utterances?: number;
      total_words?: number;
      child_words?: number;
    };
    raw_scores: Record<string, {
      value: number;
      z_score?: number | null;
      percentile?: number | null;
      age_equivalent_months?: number | null;
      sample_utterances?: number;
      minimum_sample?: number;
      max_score?: number;
    } | null>;
  }): Promise<ScoreReviewResult> => {
    const resp = await post<ScoreReviewResult>('/myslp/score-review', data);
    // Async processing — poll session for result (25 attempts × 3s = ~75s ceiling)
    // Enrichment Lambda typically takes ~50s (Bedrock call + DynamoDB write)
    if (resp.status === 202 || (resp.data as any)?.status === 'processing') {
      let consecutiveErrors = 0;
      for (let i = 0; i < 25; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const sessionResp = await get<any>(`/sessions/${data.session_id}`);
          const session = sessionResp.data;
          const review = session?.scoreReview;
          if (review && review.status !== 'processing') {
            if (review.status === 'failed') throw new Error(review.error || 'Score review failed');
            return review as ScoreReviewResult;
          }
          consecutiveErrors = 0;
        } catch (pollErr: any) {
          // Definitive application error — rethrow
          if (pollErr?.message?.includes('failed')) {
            throw pollErr instanceof Error ? pollErr : new Error(pollErr?.message || 'Score review failed');
          }
          // Network/transient error — bail after 3 consecutive failures
          consecutiveErrors++;
          if (consecutiveErrors >= 3) {
            throw new Error('Score review unreachable after multiple network errors');
          }
        }
      }
      throw new Error('Score review timed out — tap "Update Analysis" to retry');
    }
    return snakeToCamel(resp.data) as ScoreReviewResult;
  },

  /**
   * MySLP IPA Target Generation — AI-powered G2P replacement
   */
  ipaTargets: async (data: {
    session_id?: string;
    utterances: Array<{ line: number; text: string }>;
    child_age_months?: number;
    dialect?: string;
  }): Promise<IpaTargetsResult> => {
    const resp = await post<IpaTargetsResult>('/myslp/ipa-targets', data);
    return resp.data as IpaTargetsResult;
  },

  /**
   * MySLP Transcript Analysis — pragmatic, narrative, social, discourse analysis
   */
  analyzeTranscript: async (data: {
    session_id: string;
    child_age_months: number;
    child_name: string;
    transcript: string;
    verification_type: 'COMPREHENSIVE' | 'LANGUAGE_ONLY';
  }): Promise<TranscriptAnalysisResult> => {
    const resp = await post<TranscriptAnalysisResult>('/myslp/analyze-transcript', data);
    // Async processing — poll session for result (25 attempts × 3s = ~75s ceiling)
    // Enrichment Lambda typically takes ~50s (Bedrock call + DynamoDB write)
    if (resp.status === 202 || (resp.data as any)?.status === 'processing') {
      let consecutiveErrors = 0;
      for (let i = 0; i < 25; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const sessionResp = await get<any>(`/sessions/${data.session_id}`);
          const session = sessionResp.data;
          const analysis = session?.transcriptAnalysis;
          if (analysis && analysis.status !== 'processing') {
            if (analysis.status === 'failed') throw new Error(analysis.error || 'Transcript analysis failed');
            return analysis as TranscriptAnalysisResult;
          }
          consecutiveErrors = 0;
        } catch (pollErr: any) {
          // Definitive application error — rethrow
          if (pollErr?.message?.includes('failed')) {
            throw pollErr instanceof Error ? pollErr : new Error(pollErr?.message || 'Transcript analysis failed');
          }
          // Network/transient error — bail after 3 consecutive failures
          consecutiveErrors++;
          if (consecutiveErrors >= 3) {
            throw new Error('Transcript analysis unreachable after multiple network errors');
          }
        }
      }
      throw new Error('Transcript analysis timed out — tap "Update Analysis" to retry');
    }
    return snakeToCamel(resp.data) as TranscriptAnalysisResult;
  },

  /**
   * MySLP Edit Report — AI-powered one-click report editing
   */
  editReport: async (data: {
    action: 'improve_wording' | 'add_recommendations' | 'check_gaps' | 'fix_issue';
    sections: Record<string, { title: string; text: string }>;
    section_key?: string;
    issue?: { message: string; suggestion: string };
    patient_context?: { name?: string; age_months?: number };
  }): Promise<{
    action: string;
    proposed_changes: Array<{
      section_key: string;
      original_text: string;
      new_text: string;
      explanation: string;
    }>;
    change_count: number;
  }> => {
    const resp = await post<any>('/myslp/edit-report', data);
    return resp.data;
  },
};

// ============================================================================
// Progress Monitoring Types & Endpoints
// ============================================================================

export type GoalDomain = 'articulation' | 'language' | 'fluency' | 'voice' | 'pragmatics';
export type GoalStatus = 'active' | 'met' | 'discontinued';
export type ProgressRating = 'Meeting Goal' | 'Progressing Toward Goal' | 'Insufficient Progress' | 'Regression' | 'Insufficient Data';

export interface IEPGoal {
  goalId: string;
  patientId: string;
  goalText: string;
  domain: GoalDomain;
  baselineValue: string;
  baselineDate: string;
  baselineSource: string;
  targetValue: string;
  targetCriterion: string;
  status: GoalStatus;
  inputMethod: 'photo' | 'text' | 'conversation';
  sourceConversationId?: string;
  sourceImageS3Key?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProgressDataEntry {
  progressDataId: string;
  patientId: string;
  goalId: string;
  dataDate: string;
  reportingPeriod: string;
  value: string;
  valueType: 'percentage' | 'count' | 'ratio' | 'qualitative';
  trials?: number;
  opportunities?: number;
  sessionNotes: string;
  inputMethod: 'manual' | 'conversation' | 'photo';
  dataSourceDescription: string;
  createdAt: string;
}

export interface ProgressReportGoal {
  goalId: string;
  goalText: string;
  domain: string;
  baseline: { value: string; date: string; source: string };
  current: { value: string; date: string; source: string };
  percentChange: number | null;
  progressRating: ProgressRating;
  narrative: string;
  dataPoints: Array<{ date: string; value: string; source: string }>;
}

export interface ProgressReportContent {
  reportingPeriod: { start: string; end: string };
  stateCode: string;
  inputMethod: string;
  conversationId: string;
  goals: ProgressReportGoal[];
  fullReportText: string;
  complianceNotes?: string;
  attestation: {
    attested: boolean;
    attestedAt: string | null;
    clinicianId: string | null;
    attestationText: string;
  };
}

export interface ProgressReport {
  reportId: string;
  patientId: string;
  reportType: 'progress';
  content: ProgressReportContent;
  status: 'draft' | 'finalized';
  createdAt: string;
  updatedAt: string;
}

export interface GoalExtractionResult {
  goals: Array<{
    goalText: string;
    domain: GoalDomain;
    baselineValue: string | null;
    baselineDate: string | null;
    targetValue: string | null;
    targetCriterion: string | null;
  }>;
  flags: Array<{ field: string; goalIndex: number; reason: string }>;
  confidence: 'high' | 'medium' | 'low';
  notes: string;
}

export interface DataSheetExtractionResult {
  dataEntries: Array<{
    goalId: string;
    goalDescription: string;
    dataDate: string;
    value: string;
    valueType: string;
    trials: number | null;
    opportunities: number | null;
    sessionNotes: string;
  }>;
  flags: Array<{ field: string; entryIndex: number; reason: string }>;
  confidence: 'high' | 'medium' | 'low';
  notes: string;
}

export interface AutoProgressResult {
  patientId: string;
  evaluations: Array<{
    sessionId: string;
    date: string;
    scores: Record<string, { value: number; severity?: string }>;
  }>;
  evaluationCount: number;
  deltas: Record<string, {
    baseline: number;
    baselineDate: string;
    baselineSession: string;
    current: number;
    currentDate: string;
    currentSession: string;
    change: number;
    percentChange: number;
  }>;
  hasData: boolean;
  summary: string;
}

/**
 * Progress Reports — generate, list, get, attest
 */
export const progressReports = {
  generate: (data: {
    patientId: string;
    reportingPeriod: { start: string; end: string };
    goalIds?: string[];
    inputMethod?: string;
    conversationId?: string;
  }): Promise<ApiResponse<{ reportId: string; content: ProgressReportContent; status: string }>> =>
    post('/progress-reports/generate', data),

  list: (patientId: string): Promise<ApiResponse<{ reports: ProgressReport[]; count: number }>> =>
    get(`/progress-reports/${patientId}/history`),

  get: (reportId: string): Promise<ApiResponse<ProgressReport>> =>
    get(`/progress-reports/${reportId}`),

  attest: (reportId: string, attestationText?: string): Promise<ApiResponse<{
    reportId: string;
    status: string;
    attestedAt: string;
  }>> =>
    post(`/progress-reports/${reportId}/attest`, { attestationText }),

  autoGenerate: (patientId: string): Promise<ApiResponse<AutoProgressResult>> =>
    get(`/progress-reports/${patientId}/auto`),
};

/**
 * IEP Goals — CRUD + photo extraction
 */
export const goals = {
  create: (data: {
    patientId: string;
    goalText: string;
    domain: GoalDomain;
    baselineValue?: string;
    baselineDate?: string;
    baselineSource?: string;
    targetValue?: string;
    targetCriterion?: string;
    inputMethod?: string;
    sourceConversationId?: string;
  }): Promise<ApiResponse<IEPGoal>> =>
    post('/goals', data),

  list: (patientId: string, status?: GoalStatus): Promise<ApiResponse<{ goals: IEPGoal[]; count: number }>> =>
    get(`/goals/${patientId}${status ? `?status=${status}` : ''}`),

  update: (goalId: string, data: {
    patientId: string;
    goalText?: string;
    domain?: GoalDomain;
    baselineValue?: string;
    targetValue?: string;
    targetCriterion?: string;
    status?: GoalStatus;
  }): Promise<ApiResponse<IEPGoal>> =>
    put(`/goals/${goalId}`, data),

  uploadImage: (data: {
    image: string;
    contentType?: string;
    patientId?: string;
  }): Promise<ApiResponse<GoalExtractionResult>> =>
    post('/goals/upload-image', data),
};

/**
 * Progress Data — session-by-session data entries + photo extraction
 */
export const progressData = {
  create: (data: {
    patientId: string;
    goalId: string;
    dataDate: string;
    value: string;
    valueType?: string;
    reportingPeriod?: string;
    trials?: number;
    opportunities?: number;
    sessionNotes?: string;
    inputMethod?: string;
    dataSourceDescription?: string;
  }): Promise<ApiResponse<ProgressDataEntry>> =>
    post('/progress-data', data),

  list: (patientId: string, goalId?: string): Promise<ApiResponse<{ entries: ProgressDataEntry[]; count: number }>> =>
    get(`/progress-data/${patientId}${goalId ? `?goalId=${goalId}` : ''}`),

  uploadImage: (data: {
    image: string;
    contentType?: string;
    patientId: string;
  }): Promise<ApiResponse<DataSheetExtractionResult>> =>
    post('/progress-data/upload-image', data),
};

// ============================================================================
// Image Generation Endpoints
// ============================================================================

export interface ImageGenerateRequest {
  prompt: string;
  tier?: 'standard' | 'premium';
  style?: 'flashcard' | 'scene' | 'sequence' | 'minimal_pair';
  sessionId?: string;
}

export interface ImageUsage {
  /** Free images consumed in the current session (0–5). */
  sessionFreeUsed: number;
  /** Free images remaining in the current session. Starts at 5. */
  sessionFreeRemaining: number;
  /** Session free-image limit (currently 5). */
  sessionFreeLimit: number;
  /** Global token balance (shared across sessions). */
  tokensRemaining: number;
  /** Token-funded images generated in this session. */
  sessionTokenFunded: number;
  /** Images remaining in the current paid batch of 5 before the next token charge. */
  sessionTokenBatchRemaining: number;
  canGenerate: boolean;
}

export interface SequenceImage {
  imageUrl: string;
  generationId: string;
  prompt: string;
  step: number;
}

export interface ImageGenerateResponse {
  imageUrl: string;
  prompt: string;
  tier: 'standard' | 'premium';
  generationId: string;
  usage: ImageUsage;
  /** Present when style is "sequence" — contains all images in the narrative */
  images?: SequenceImage[];
}

export interface ImageCreditsResponse {
  userId: string;
  standardTier: { sessionCap: number; description: string };
  premiumTier: { creditsRemaining: number; imagesPerCredit: number; imagesRemaining: number };
}

export interface ImageHistoryEntry {
  generationId: string;
  imageUrl: string;
  prompt: string;
  timestamp: string;
  source: 'free' | 'token';
}

export const images = {
  generate: (data: ImageGenerateRequest): Promise<ApiResponse<ImageGenerateResponse>> =>
    post('/images/generate', data),

  /** Convenience: generate with just a prompt (standard tier) */
  generateSimple: (prompt: string): Promise<ApiResponse<ImageGenerateResponse>> =>
    post('/images/generate', { prompt }),

  getCredits: (): Promise<ApiResponse<ImageCreditsResponse>> =>
    get('/images/credits'),

  getHistory: (limit?: number): Promise<ApiResponse<{ generations: ImageHistoryEntry[] }>> =>
    get(`/images/history${limit ? `?limit=${limit}` : ''}`),

  /** Generate image from MySLP chat context */
  generateFromChat: (data: ImageGenerateRequest): Promise<ApiResponse<ImageGenerateResponse>> =>
    post('/myslp/generate-image', data),

  /** Get image usage scoped to the given MySLP session (5 free per session). */
  getUsage: (sessionId?: string): Promise<ApiResponse<ImageUsage>> =>
    get(`/images/usage${sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : ''}`),

  getSessionUsage: (sessionId: string): Promise<ApiResponse<ImageUsage>> =>
    get(`/images/usage?sessionId=${encodeURIComponent(sessionId)}`),

  deleteImage: (imageId: string): Promise<ApiResponse<{ generationId: string; deleted: boolean }>> =>
    del(`/images/${imageId}`),
};

// ============================================================================
// PDF Export Endpoints (ExportHandler)
// ============================================================================

export const pdfExport = {
  /**
   * Create PDF export
   *
   * POST /export/pdf → { exportId, download_url, file_data, ... }
   */
  create: (reportId: string, password?: string, previewState?: {
    student_info?: { firstName: string; lastName: string; dateOfBirth: string; ageDisplay?: string };
    include_references?: boolean;
    cim_flags?: string[];
  }): Promise<ApiResponse<{
    export_id: string;
    status: string;
    download_url?: string;
    file_size_bytes?: number;
    format?: string;
    file_data?: string;
    content_type?: string;
    filename?: string;
  }>> =>
    post('/export/pdf', {
      report_id: reportId,
      ...(password ? { encryption: { password } } : {}),
      ...(previewState?.student_info ? { student_info: previewState.student_info } : {}),
      ...(previewState?.include_references ? { include_references: true } : {}),
      ...(previewState?.cim_flags?.length ? { cim_flags: previewState.cim_flags } : {}),
    }),

  /**
   * Check export status
   */
  getStatus: (exportId: string): Promise<ApiResponse<{
    export_id: string;
    status: string;
    download_url?: string;
    completed_at?: string;
    file_size_bytes?: number;
  }>> =>
    get(`/export/${exportId}/status`),

  /**
   * Get download URL for completed export
   */
  download: (exportId: string): Promise<ApiResponse<{
    export_id: string;
    download_url: string;
    filename: string;
    format?: string;
    expires_in: number;
  }>> =>
    get(`/export/${exportId}/download`),

  /**
   * Retrieve stored password for a completed export
   */
  getPassword: (exportId: string): Promise<ApiResponse<{
    export_id: string;
    password: string;
  }>> =>
    get(`/export/${exportId}/password`),
};

// ============================================================================
// Email Delivery Endpoints (EmailHandler — SES)
// ============================================================================

export interface EmailRecipient {
  email: string;
  name: string;
}

export const emailDelivery = {
  /**
   * Send report via email
   */
  send: (data: {
    reportId: string;
    recipients: EmailRecipient[];
    subject?: string;
    message?: string;
    senderName?: string;
    password?: string;
  }): Promise<ApiResponse<{
    email_id: string;
    status: string;
  }>> =>
    post('/email/send', {
      report_id: data.reportId,
      recipients: data.recipients,
      subject: data.subject,
      message: data.message,
      sender_name: data.senderName,
      password: data.password,
    }),

  /**
   * Check email delivery status
   */
  getStatus: (emailId: string): Promise<ApiResponse<{
    email_id: string;
    status: string;
    recipients: EmailRecipient[];
  }>> =>
    get(`/email/${emailId}/status`),
};

// ============================================================================
// Subscription & Token Types
// ============================================================================

export interface AccessGrant {
  accessLevel: string;
  grantedBy: string;
  grantedAt: string;
  expiresAt?: string | null;
  reason: string;
}

export interface SubscriptionInfo {
  hasSubscription: boolean;
  hasFullAccess?: boolean;
  accessGrant?: AccessGrant | null;
  userId: string;
  email: string;
  plan: string;
  status: 'active' | 'expired' | 'deactivated' | 'grant' | 'none';
  purchaseDate: string;
  expirationDate: string;
  daysRemaining: number;
  isExpired: boolean;
  tokensAllocated: number;
  tokensUsed: number;
  tokensRemaining: number;
  isGroupMember: boolean;
  groupId?: string | null;
  groupName?: string;
  isGroupLeader: boolean;
  groupTokensTotal?: number;
  groupTokensUsed?: number;
  groupTokensRemaining?: number;
  renewUrl: string;
  buyTokensUrl?: string;
}

export interface TokenTransaction {
  transactionId: string;
  userId: string;
  type: 'allocate' | 'consume' | 'refund';
  evaluationId?: string;
  reason: string;
  timestamp: string;
}

export interface GroupMember {
  userId: string;
  email: string;
  status: 'active' | 'invited' | 'deactivated';
  joinedAt: string;
}

export interface GroupUsage {
  groupId: string;
  tokensTotal: number;
  tokensUsed: number;
  tokensRemaining: number;
  memberCount: number;
  members: Array<{
    userId: string;
    email: string;
    tokensUsed: number;
  }>;
}

// ============================================================================
// Subscription Endpoints
// ============================================================================

export const subscription = {
  getMySubscription: (): Promise<ApiResponse<SubscriptionInfo>> =>
    get('/subscriptions/me'),

  getMyTransactions: (limit?: number): Promise<ApiResponse<TokenTransaction[]>> =>
    get('/subscriptions/me/transactions', { params: { limit } }),
};

// ============================================================================
// Group Management Endpoints
// ============================================================================

export const groups = {
  getMembers: (groupId: string): Promise<ApiResponse<{
    groupId: string;
    groupName?: string;
    groupSize: number;
    members: GroupMember[];
  }>> =>
    get(`/groups/${groupId}/members`),

  getUsage: (groupId: string): Promise<ApiResponse<GroupUsage>> =>
    get(`/groups/${groupId}/usage`),

  invite: (groupId: string, email: string, firstName?: string, lastName?: string): Promise<ApiResponse<GroupMember>> =>
    post(`/groups/${groupId}/invite`, { email, first_name: firstName, last_name: lastName }),

  deactivate: (groupId: string, userId: string): Promise<ApiResponse<void>> =>
    post(`/groups/${groupId}/deactivate/${userId}`),
};

// ============================================================================
// Feedback Endpoints
// ============================================================================

export const feedback = {
  submit: (data: { message: string; rating?: number; page?: string }): Promise<ApiResponse<{ feedbackId: string }>> =>
    post('/feedback', data),
};

// ============================================================================
// Admin Endpoints
// ============================================================================

export interface BaaRecord {
  userId: string;
  email: string;
  organizationName: string;
  signerName: string;
  signerTitle: string;
  version: string;
  acceptedAt: string;
  ipAddress: string;
}

export interface BaaRecordsResponse {
  records: BaaRecord[];
  total: number;
}

export interface AdminAccessGrant {
  email: string;
  accessLevel: string;
  grantedBy: string;
  grantedAt: string;
  expiresAt?: string | null;
  reason: string;
}

export type AccountType = 'customer' | 'employee' | 'test' | 'beta';

export interface AdminUser {
  userId: string;
  cognitoUsername: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  accountType: AccountType;
  status: string;
  enabled: boolean;
  cognitoStatus: string;
  role: string;
  subscriptionStatus: string;
  tokensAllocated: number;
  tokensUsed: number;
  tokensRemaining: number;
  plan: string;
  expirationDate: string;
  subscriptionId: string;
  notes: string;
  signatureBlock: string;
  createdAt: string;
}

export interface AdminUsersSummary {
  totalUsers: number;
  activeUsers: number;
  totalTokensRemaining: number;
  customerCount: number;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  summary: AdminUsersSummary;
}

export const admin = {
  listAccessGrants: (): Promise<ApiResponse<AdminAccessGrant[]>> =>
    get('/admin/access-grants'),

  createAccessGrant: (data: {
    email: string;
    access_level?: string;
    reason?: string;
    expires_days?: number;
  }): Promise<ApiResponse<AdminAccessGrant>> =>
    post('/admin/access-grants', data),

  revokeAccessGrant: (email: string): Promise<ApiResponse<{ email: string; revoked: boolean }>> =>
    del('/admin/access-grants', { data: { email } }),

  listUsers: (): Promise<ApiResponse<AdminUsersResponse>> =>
    get('/admin/users'),

  createUser: (data: {
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    token_count: number;
    subscription_length: string;
    notes: string;
    send_welcome_email: boolean;
    account_type?: string;
  }): Promise<ApiResponse<any>> =>
    post('/admin/users', data),

  grantTokens: (userId: string, tokenCount: number, reason: string, email?: string): Promise<ApiResponse<any>> =>
    post(`/admin/users/${userId}/tokens`, { token_count: tokenCount, reason, ...(email ? { email } : {}) }),

  updateStatus: (userId: string, action: 'activate' | 'deactivate'): Promise<ApiResponse<any>> =>
    post(`/admin/users/${userId}/status`, { action }),

  resendWelcome: (userId: string): Promise<ApiResponse<any>> =>
    post(`/admin/users/${userId}/welcome-email`, {}),

  resetPassword: (userId: string): Promise<ApiResponse<any>> =>
    post(`/admin/users/${userId}/reset-password`, {}),

  editUser: (userId: string, data: {
    role?: string;
    notes?: string;
    subscription_length?: string;
    first_name?: string;
    last_name?: string;
    signature_block?: string;
    email?: string;
    account_type?: string;
  }): Promise<ApiResponse<any>> =>
    post(`/admin/users/${userId}/edit`, data),

  deleteUser: (userId: string): Promise<ApiResponse<{ userId: string; email: string; deleted: boolean }>> =>
    post(`/admin/users/${userId}/delete`, {}),

  bulkValidate: (emails: string[]): Promise<ApiResponse<{
    results: Array<{ email: string; status: string; reason: string }>;
    summary: { total: number; valid: number; existing: number; invalid: number; duplicate: number };
  }>> =>
    post('/admin/users/bulk-validate', { emails }),

  listBaaRecords: (): Promise<ApiResponse<BaaRecordsResponse>> =>
    get('/agreements/admin/baa-records'),

  getPipelineStats: (): Promise<ApiResponse<{
    evaluationsCompleted24h: number;
    sessionsStuckCreated: number;
    sessionsInProgress: number;
  }>> =>
    get('/admin/pipeline-stats'),
};

// ============================================================================
// Audio Upload Helper
// ============================================================================

/**
 * Upload audio file for a session
 *
 * Uses presigned URL for direct S3 upload (bypasses API Gateway size limits)
 */
export async function uploadSessionAudio(
  sessionId: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<ApiResponse<Session>> {
  // 1. Get presigned upload URL
  const { data: uploadData } = await sessions.getUploadUrl(sessionId, file.type);

  // 2. Upload directly to S3
  await fetch(uploadData.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  // Report progress (simplified - S3 doesn't support progress with fetch)
  if (onProgress) {
    onProgress(100);
  }

  // 3. Confirm upload with backend
  return sessions.confirmUpload(sessionId, uploadData.fileKey);
}

// ============================================================================
// User/Profile Endpoints
// ============================================================================

export const user = {
  /**
   * Get current user profile
   */
  getProfile: (): Promise<ApiResponse<{
    userId: string;
    email: string;
    name: string;
    roles: string[];
    organizationId?: string;
    signatureBlock?: string;
    signatureLocked?: boolean;
    licenseNumber?: string;
    ashaNumber?: string;
  }>> => get('/user/profile'),

  /**
   * Update user profile (signatureBlock, licenseNumber, ashaNumber)
   */
  updateProfile: (data: {
    signatureBlock?: string;
    licenseNumber?: string;
    ashaNumber?: string;
  }): Promise<ApiResponse<{ signatureBlock?: string; licenseNumber?: string; ashaNumber?: string }>> =>
    put('/user/profile', data),

  /**
   * Submit a support request
   */
  submitSupportRequest: (data: { subject: string; message: string }): Promise<ApiResponse<{ request_id: string }>> =>
    post('/user/support-request', data),

  /**
   * Get user preferences
   */
  getPreferences: (): Promise<ApiResponse<Record<string, unknown>>> =>
    get('/user/preferences'),

  /**
   * Update user preferences
   */
  updatePreferences: (preferences: Record<string, unknown>): Promise<ApiResponse<void>> =>
    put('/user/preferences', preferences),
};

// ============================================================================
// Health Check
// ============================================================================

export const health = {
  /**
   * Check API health (unauthenticated)
   */
  check: (): Promise<ApiResponse<{ status: string; timestamp: string }>> =>
    get('/health', { skipAuth: true } as any),
};

// ============================================================================
// District Report Types & Endpoints
// ============================================================================

export interface TemplateSection {
  id: string;
  title: string;
  parent_id?: string | null;
  type: 'narrative' | 'structured' | 'list' | 'fields' | 'table' | 'checklist';
  description?: string;
  guidance?: string;
  required: boolean;
  boilerplate?: string;
  boilerplate_blocks?: Array<{ text: string; preserve_verbatim: boolean }>;
  table_columns?: string[] | null;
  checklists?: Array<{ id: string; options: string[] }> | null;
  conditional?: boolean;
  conditional_description?: string | null;
}

export interface TemplateSchema {
  template_name: string;
  template_type?: 'evaluation_report' | 'iep' | 'progress_report' | 'other';
  total_sections?: number;
  sections: TemplateSection[];
  boilerplate_blocks?: Array<{ location: string; text: string; preserve_verbatim: boolean }>;
  field_definitions?: string[];
}

export interface TemplateListItem {
  templateId: string;
  templateName: string;
  createdAt: string;
  sectionCount: number;
  originalFileType?: string;
}

export interface NewReportRequest {
  patientId: string;
  templateId: string;
  priorReportId?: string;
  reportType?: string;
  reportingPeriod?: { start: string; end: string };
  goalDispositions?: Record<string, { status: string; notes?: string }>;
  generateNarratives?: boolean;
}

export interface NewReportResponse {
  reportId: string;
  patientId: string;
  templateId: string;
  fieldValues: Record<string, string | boolean>;
  filledCount: number;
  totalCount: number;
  previewHtmlUrl: string | null;
  status: string;
}

export interface ReportSection {
  id: string;
  title: string;
  content: string;
  required: boolean;
}

export interface SectionApprovalStatus {
  approved: boolean;
  approvedAt?: string;
  editedByClinicianAfterGeneration: boolean;
}

export interface DistrictReport {
  reportId: string;
  clinicianId: string;
  patientId: string;
  templateId: string;
  studentInfo: Record<string, string>;
  testScores: Array<Record<string, any>>;
  eligibility: Record<string, any>;
  generatedSections: ReportSection[];
  approvalStatus: Record<string, SectionApprovalStatus>;
  status: 'generating' | 'generated' | 'approved' | 'failed';
  generationStage?: string;
  generationMessage?: string;
  generationProgress?: number;
  sourceMap?: Record<string, Array<{ type: string; detail: string }>>;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  templateFileType?: 'docx' | 'pdf' | 'other' | '';
}

export interface EligibilityAnalysis {
  state: string;
  state_criteria_summary: string;
  score_analysis: Array<{
    test: string;
    score: number;
    sd_from_mean: number;
    state_threshold: number;
    meets_threshold: boolean;
    note: string;
  }>;
  category_interaction: {
    other_eligibilities: string[];
    recommendation: string;
    related_service_option: boolean;
  };
  clinical_judgment_applicable: boolean;
  clinical_judgment_rationale: string;
  bilingual_flag: boolean;
  overall_analysis: string;
  state_code_citations: string[];
  eligibility_options: string[];
}

export const districtReports = {
  listReports: (params?: { status?: string }): Promise<ApiResponse<any>> =>
    get('/district-reports', { params }),

  deleteReport: (reportId: string): Promise<ApiResponse<{ reportId: string; deleted: boolean }>> =>
    del(`/district-reports/${reportId}`),

  analyzeTemplate: (data: {
    templateFileKey: string;
    templateName: string;
    contentType: string;
  }): Promise<ApiResponse<{ jobId: string; status: string }>> =>
    post('/district-reports/analyze-template', data),

  getTemplates: (): Promise<ApiResponse<{ templates: TemplateListItem[]; count: number }>> =>
    get('/district-reports/templates'),

  deleteTemplate: (templateId: string): Promise<ApiResponse<{ templateId: string; deleted: boolean }>> =>
    del(`/district-reports/templates/${templateId}`),

  generateReport: (data: {
    templateId: string;
    reportType?: string;
    studentInfo: Record<string, string>;
    testScores: Array<Record<string, any>>;
    documents?: Array<Record<string, any>>;
    eligibility?: Record<string, any>;
    patientId?: string;
    languageSampleSessionId?: string;
    clinicalObservations?: string;
    clinicalNotes?: string;
    progressGoals?: Array<Record<string, any>>;
    reportingPeriod?: string;
  }): Promise<ApiResponse<{ reportId: string; status: string; templateId: string }>> =>
    post('/district-reports/generate', data),

  getReport: (reportId: string): Promise<ApiResponse<DistrictReport>> =>
    get(`/district-reports/${reportId}`),

  approveSection: (reportId: string, sectionId: string): Promise<ApiResponse<{
    reportId: string;
    sectionId: string;
    approved: boolean;
    allApproved: boolean;
  }>> =>
    put(`/district-reports/${reportId}/sections/${sectionId}/approve`),

  updateSection: (reportId: string, sectionId: string, content: string): Promise<ApiResponse<{
    reportId: string;
    sectionId: string;
  }>> =>
    put(`/district-reports/${reportId}/sections/${sectionId}`, { content }),

  analyzeEligibility: (data: {
    state: string;
    evaluationType: string;
    testScores: Array<Record<string, any>>;
    studentAge?: string;
    grade?: string;
    currentEligibility?: string;
    languages?: string[];
    languageSample?: Record<string, any>;
  }): Promise<ApiResponse<{ jobId: string; status: string }>> =>
    post('/district-reports/analyze-eligibility', data),

  getUploadUrl: (data: {
    filename: string;
    contentType: string;
  }): Promise<ApiResponse<{ uploadUrl: string; fileKey: string }>> =>
    post('/district-reports/template-upload-url', data),

  extractHandwritten: (data: {
    fileKey: string;
    contentType: string;
  }): Promise<ApiResponse<{ extractedText: string; confidence: number }>> =>
    post('/district-reports/extract-handwritten', data),

  attestReport: (reportId: string, data: {
    signerName: string;
    signerCredentials: string;
    signerLicense?: string;
    signerAsha?: string;
    signerDate?: string;
  }): Promise<ApiResponse<{ reportId: string; attested: boolean; attestedAt: string }>> =>
    post(`/district-reports/${reportId}/attest`, data),

  getAuditTrail: (reportId: string): Promise<ApiResponse<{
    trail: Array<{
      timestamp: string;
      action: string;
      sectionId?: string;
      clinicianId: string;
      contentHash?: string;
      signerName?: string;
      signerCredentials?: string;
    }>;
    attestation?: {
      signer_name: string;
      signer_credentials: string;
      attested_at: string;
      clinician_id: string;
    };
  }>> =>
    get(`/district-reports/${reportId}/audit-trail`),

  extractDocumentData: (data: {
    documentId: string;
    patientId: string;
  }): Promise<ApiResponse<{ jobId: string; status: string }>> =>
    post('/district-reports/extract-document-data', data),

  getJobStatus: (jobId: string): Promise<ApiResponse<{
    jobId: string;
    jobType: string;
    status: string;
    progress: number;
    stageMessage: string;
    result: any;
    error: string | null;
  }>> =>
    get(`/district-reports/jobs/${jobId}`),

  generateIepGoals: (reportId: string): Promise<ApiResponse<{ jobId: string; status: string }>> =>
    post(`/district-reports/${reportId}/generate-iep-goals`),

  generateTherapyIdeas: (reportId: string): Promise<ApiResponse<{ jobId: string; status: string }>> =>
    post(`/district-reports/${reportId}/generate-therapy-ideas`),

  /**
   * Export report as filled DOCX using original template.
   * Falls back to standard DOCX if template injection fails.
   */
  exportDocxTemplate: (reportId: string, password?: string): Promise<ApiResponse<{
    file_data: string;
    content_type: string;
    filename: string;
    format: string;
    template_injected: boolean;
  }>> =>
    post(`/district-reports/${reportId}/export/docx`, {
      ...(password ? { encryption: { password } } : {}),
    }),

  checkCoherence: (reportId: string): Promise<ApiResponse<{
    suggestions: string[];
    checked: boolean;
  }>> =>
    post(`/district-reports/${reportId}/check-coherence`),
};

// ============================================================================
// Scheduler Endpoints
// ============================================================================

export const scheduler = {
  list: (): Promise<ApiResponse<any[]>> =>
    get('/schedules'),

  get: (scheduleId: string): Promise<ApiResponse<any>> =>
    get(`/schedules/${scheduleId}`),

  create: (data: {
    school_year?: string;
    slp_availability?: Record<string, any>;
    school_constraints?: Record<string, any>;
    students?: Array<Record<string, any>>;
    grouping_prefs?: { max_group_size?: number; max_grade_gap?: number };
  }): Promise<ApiResponse<{ schedule_id: string; status: string }>> =>
    post('/schedules', data),

  update: (scheduleId: string, data: Record<string, any>): Promise<ApiResponse<{ schedule_id: string }>> =>
    put(`/schedules/${scheduleId}`, data),

  delete: (scheduleId: string): Promise<ApiResponse<void>> =>
    del(`/schedules/${scheduleId}`),

  build: (scheduleId: string): Promise<ApiResponse<{ job_id: string; status_url: string }>> =>
    post(`/schedules/${scheduleId}/build`, {}),

  rebuild: (scheduleId: string): Promise<ApiResponse<{ job_id: string; status_url: string }>> =>
    post(`/schedules/${scheduleId}/rebuild`, {}),

  // ---- Scheduler 2.0: Paste-first extraction ----
  extractPaste: (data: { text: string }): Promise<ApiResponse<any>> =>
    post('/schedules/extract-paste', data),

  extractDocument: (data: { document_key: string; file_type: string }): Promise<ApiResponse<any>> =>
    post('/schedules/extract-document', data),

  getDocumentUploadUrl: (data: { filename: string; contentType: string }): Promise<ApiResponse<{ uploadUrl: string; fileKey: string }>> =>
    post('/schedules/document-upload-url', data),

  mergeExtraction: (data: { schedule_id: string; text?: string; document_key?: string; file_type?: string }): Promise<ApiResponse<any>> =>
    post('/schedules/merge', data),

  // ---- Scheduler 2.0: Conversational gap-filling ----
  startConversation: (scheduleId: string): Promise<ApiResponse<any>> =>
    post(`/schedules/${scheduleId}/conversation`, {}),

  sendConversationMessage: (scheduleId: string, data: { message: string; conversation_history: Array<{ role: string; content: string }> }): Promise<ApiResponse<any>> =>
    post(`/schedules/${scheduleId}/conversation`, data),

  // ---- Scheduler 2.0: Alternatives ----
  selectAlternative: (scheduleId: string, index: number): Promise<ApiResponse<any>> =>
    post(`/schedules/${scheduleId}/alternatives/select`, { index }),

  // ---- Scheduler 2.0: Session moves ----
  moveSession: (scheduleId: string, sessionId: string, data: { day: string; start_time: string; override_warnings?: boolean }): Promise<ApiResponse<any>> =>
    put(`/schedules/${scheduleId}/sessions/${sessionId}/move`, data),

  // ---- Scheduler 2.0: Rescheduling ----
  reschedule: (scheduleId: string, data: Record<string, any>): Promise<ApiResponse<any>> =>
    post(`/schedules/${scheduleId}/reschedule`, data),
};

// ============================================================================
// TTS (Text-to-Speech via AWS Polly)
// ============================================================================

export const tts = {
  synthesize: (data: {
    text?: string;
    phoneme?: string;
    voice_id?: string;
    engine?: string;
  }): Promise<ApiResponse<{
    audioBase64: string;
    contentType: string;
    voiceId: string;
    fallback?: boolean;
    reason?: string;
    usage?: {
      pollyTextRequestsUsed: number;
      pollyTextRequestsLimit: number;
      remaining: number;
      resetDate: string;
      additionalCredits: number;
    };
  }>> => post('/tts/synthesize', data),

  getUsage: (): Promise<ApiResponse<{
    pollyTextRequestsUsed: number;
    pollyTextRequestsLimit: number;
    remaining: number;
    resetDate: string;
    additionalCredits: number;
  }>> => get('/tts/usage'),
};

// ============================================================================
// Agreement Types & Endpoints
// ============================================================================

export interface AgreementStatus {
  tosAccepted: boolean;
  tosVersion: string;
  tosAcceptedAt: string | null;
  baaSelectionMade: boolean;
  baaRequired: boolean | null;
  baaSelection: string | null;
  baaAccepted: boolean | null;
  baaVersion: string;
  baaAcceptedAt: string | null;
}

export interface TosAcceptRequest {
  version: string;
  scroll_completed: boolean;
  full_name: string;
  user_agent: string;
}

export interface BaaAcceptRequest {
  version: string;
  scroll_completed: boolean;
  organization_name: string;
  signer_name: string;
  signer_title: string;
  user_agent: string;
}

export interface BaaSelectionRequest {
  selection: 'yes' | 'no_ferpa_ndpa' | 'unsure';
}

export interface BaaSelectionResponse {
  selection: string;
  baaRequired: boolean;
}

export const agreements = {
  getStatus: (): Promise<ApiResponse<AgreementStatus>> =>
    get('/agreements/status'),

  acceptTos: (data: TosAcceptRequest): Promise<ApiResponse<void>> =>
    post('/agreements/tos-accept', data),

  acceptBaa: (data: BaaAcceptRequest): Promise<ApiResponse<void>> =>
    post('/agreements/baa-accept', data),

  recordBaaSelection: (data: BaaSelectionRequest): Promise<ApiResponse<BaaSelectionResponse>> =>
    post('/agreements/baa-selection', data),
};

// ============================================================================
// Templates
// ============================================================================

export interface TemplateField {
  fieldId: string;
  label: string;
  type: 'text' | 'dropdown' | 'checkbox' | 'date' | 'combo_box';
  section: string;
  options: string[];
}

export interface TemplateSummary {
  templateId: string;
  templateName: string;
  fieldCount: number;
  sectionCount: number;
  tableCount: number;
  createdAt: string;
  updatedAt?: string;
  /** Optional type discriminator — 'template' for actual templates */
  type?: string;
}

export interface TemplateUploadResponse {
  templateId: string;
  templateName: string;
  fieldCount: number;
  sectionCount: number;
  tableCount: number;
  previewHtmlUrl: string | null;
  fields: TemplateField[];
}

export interface TemplateDetail extends TemplateSummary {
  fields: TemplateField[];
  previewHtmlUrl: string | null;
  /** Presigned URL for the original .docx — use with JSZip for client-side preview rendering. */
  docxUrl?: string | null;
  schema: Record<string, unknown>;
}

export interface TemplatePopulateRequest {
  clinicalData?: Array<{ source: string; content: string; filename?: string }>;
  patientInfo?: Record<string, unknown>;
  sessionInfo?: Record<string, unknown>;
  patientId?: string;
  sessionId?: string;
}

export interface TemplatePopulateResponse {
  templateId: string;
  fieldValues: Record<string, string | boolean>;
  filledCount: number;
  totalCount: number;
  populatedDocxUrl: string | null;
  previewHtmlUrl: string | null;
  populateTimeSec: number;
}

export const templates = {
  upload: (file: File, onProgress?: (p: number) => void): Promise<ApiResponse<TemplateUploadResponse>> =>
    uploadFile('/templates/upload', file, onProgress),

  list: (): Promise<ApiResponse<{ templates: TemplateSummary[] }>> =>
    get('/templates'),

  get: (templateId: string): Promise<ApiResponse<TemplateDetail>> =>
    get(`/templates/${templateId}`),

  delete: (templateId: string): Promise<ApiResponse<void>> =>
    del(`/templates/${templateId}`),

  rename: (templateId: string, templateName: string): Promise<ApiResponse<{ templateId: string; templateName: string }>> =>
    patch(`/templates/${templateId}`, { templateName }),

  generate: (data: {
    state: string;
    setting: string;
    evaluationType: string;
    discipline: string;
    studentContext?: string;
  }): Promise<ApiResponse<{ templateId: string; templateName: string; sectionCount: number; fieldCount: number }>> =>
    post('/templates/generate', data),

  populate: (templateId: string, data: TemplatePopulateRequest): Promise<ApiResponse<TemplatePopulateResponse>> =>
    post(`/templates/${templateId}/populate`, data),

  preview: (templateId: string, fieldValues: Record<string, string | boolean>): Promise<ApiResponse<{ previewHtmlUrl: string }>> =>
    post(`/templates/${templateId}/preview`, { fieldValues }),
};

// ============================================================================
// Literacy (Lightspeed Literacy Module)
// ============================================================================

export interface LiteracyProgressData {
  phases: Record<string, any>;
  currentPhaseId: string;
  currentLessonId: string;
  totalTimeSpent: number;
  lastActivity: number | null;
}

export const literacy = {
  getProgress: (patientId: string): Promise<ApiResponse<{ progress: LiteracyProgressData }>> =>
    get(`/patients/${patientId}/literacy-progress`),

  updateProgress: (patientId: string, progress: LiteracyProgressData): Promise<ApiResponse<{ progress: LiteracyProgressData }>> =>
    put(`/patients/${patientId}/literacy-progress`, { progress }),

  recordDrillAttempt: (patientId: string, data: {
    lessonId: string;
    drillType: string;
    attempt: {
      itemId: string;
      correct: boolean;
      timestamp: number;
      responseTime?: number;
    };
  }): Promise<ApiResponse<{ progress: LiteracyProgressData }>> =>
    post(`/patients/${patientId}/literacy-progress/drill-attempt`, data),
};

// ============================================================================
// Therapy Notes
// ============================================================================

export interface TherapyNote {
  patientId: string;
  sessionTimestamp: string;
  clinicianId: string;
  sessionEnd: string;
  durationMinutes: number;
  recordingS3Key: string;
  transcriptS3Key: string;
  diarizationResult: { segments?: Array<{ speaker: string; text: string; start_time: number; end_time: number }> };
  inputMode: 'ambient' | 'dictation' | 'tap_check' | 'manual' | 'telehealth_import';
  goalsTargeted: TherapyGoalTarget[];
  soapNote: { subjective: string; objective: string; assessment: string; plan: string };
  rawDraft: string;
  approvedNote: string;
  approvalStatus: 'draft' | 'approved' | 'needs_review';
  approvedAt: string;
  approvedBy: string;
  serviceType: string;
  serviceCode: string;
  location: string;
  noShow: boolean;
  noShowContactAttempts: { timestamp: string; method: string; result: string }[];
  sessionType: string;
  groupMembers: string[];
  parentPresent: boolean;
  parentParticipated: boolean;
  billingStatus: string;
  mediCalFields: Record<string, unknown>;
  processingStatus: 'processing' | 'completed' | 'failed' | '';
  processingError: string;
  createdAt: string;
  updatedAt: string;
}

export interface TherapyGoalTarget {
  goalId: string;
  goalText: string;
  accuracyPct: number | null;
  cueingLevel: string;
  trials: number | null;
  notes: string;
}

export interface TherapyGoal {
  patientId: string;
  goalId: string;
  clinicianId: string;
  goalText: string;
  goalArea: string;
  baseline: string;
  targetAccuracy: number;
  targetConsistency: string;
  measurementMethod: string;
  reportingDate: string;
  conditions: string;
  status: 'active' | 'met' | 'not_met' | 'discontinued' | 'modified';
  createdFromIep: boolean;
  iepSourceId: string;
  progressData: { date: string; accuracy: number; cueingLevel: string; trials: number; notes: string }[];
  createdAt: string;
  updatedAt: string;
}

export const therapy = {
  /** Get presigned URL for therapy audio upload */
  getUploadUrl: (patientId: string, contentType: string): Promise<ApiResponse<{ uploadUrl: string; s3Key: string; sessionTimestamp: string }>> =>
    post('/therapy/upload-url', { patientId, contentType }),

  /** Get presigned multipart upload URLs — init + per-part PUT URLs + complete/abort URLs */
  getMultipartUploadUrl: (patientId: string, contentType: string, partCount: number): Promise<ApiResponse<{
    multipart: true;
    uploadId: string;
    s3Key: string;
    bucket: string;
    sessionTimestamp: string;
    partUrls: Array<{ partNumber: number; url: string }>;
    completeUrl: string;
    abortUrl: string;
  }>> =>
    post('/therapy/upload-url', { patientId, contentType, multipart: true, partCount }),

  /** Create a therapy note (all modes) */
  createNote: (data: {
    patientId: string;
    inputMode: 'ambient' | 'dictation' | 'tap_check';
    sessionTimestamp?: string;
    sessionEnd?: string;
    durationMinutes?: number;
    serviceType?: string;
    serviceCode?: string;
    location?: string;
    sessionType?: string;
    groupMembers?: string[];
    parentPresent?: boolean;
    parentParticipated?: boolean;
    goalsTargeted?: TherapyGoalTarget[];
    recordingS3Key?: string;
    dictationText?: string;
    voiceMemo?: string;
  }): Promise<ApiResponse<TherapyNote>> =>
    post('/therapy/notes', data),

  /** List notes for a patient */
  listNotes: (patientId: string, params?: { startDate?: string; endDate?: string; limit?: number }): Promise<ApiResponse<TherapyNote[]>> =>
    get(`/therapy/notes/${patientId}`, { params }),

  /** Get a specific note */
  getNote: (patientId: string, sessionTimestamp: string): Promise<ApiResponse<TherapyNote>> =>
    get(`/therapy/notes/${patientId}/${encodeURIComponent(sessionTimestamp)}`),

  /** Update a note */
  updateNote: (patientId: string, sessionTimestamp: string, data: Partial<TherapyNote>): Promise<ApiResponse<TherapyNote>> =>
    put(`/therapy/notes/${patientId}/${encodeURIComponent(sessionTimestamp)}`, data),

  /** Approve a single note */
  approveNote: (patientId: string, sessionTimestamp: string, approvedNote?: string): Promise<ApiResponse<TherapyNote>> =>
    post(`/therapy/notes/${patientId}/${encodeURIComponent(sessionTimestamp)}/approve`, { approvedNote }),

  /** Delete a therapy note (for ghost/failed notes that can't be recovered) */
  deleteNote: (patientId: string, sessionTimestamp: string): Promise<ApiResponse<null>> =>
    del(`/therapy/notes/${patientId}/${encodeURIComponent(sessionTimestamp)}`),

  /** Retry processing for a failed therapy note */
  retryNote: (patientId: string, sessionTimestamp: string): Promise<ApiResponse<null>> =>
    post(`/therapy/notes/${patientId}/${encodeURIComponent(sessionTimestamp)}/retry`, {}),

  /** Get draft notes inbox */
  getInbox: (limit?: number): Promise<ApiResponse<TherapyNote[]>> =>
    get('/therapy/inbox', { params: { limit } }),

  /** Batch approve multiple notes */
  batchApprove: (notes: { patientId: string; sessionTimestamp: string }[]): Promise<ApiResponse<{ approvedCount: number; notes: TherapyNote[] }>> =>
    post('/therapy/batch-approve', { notes }),

  /** Record no-show */
  recordNoShow: (data: {
    patientId: string;
    sessionTimestamp?: string;
    contactAttempts: { timestamp: string; method: string; result: string }[];
    serviceType?: string;
    serviceCode?: string;
  }): Promise<ApiResponse<{ note: TherapyNote; consecutiveNoShows: number; escalation: string | null }>> =>
    post('/therapy/no-show', data),

  /** Sync IEP goals to therapy goals */
  syncGoals: (patientId: string): Promise<ApiResponse<{ syncedCount: number; goals: TherapyGoal[] }>> =>
    post('/therapy/sync-goals', { patientId }),

  /** Get service minutes summary */
  getServiceSummary: (patientId: string, startDate: string, endDate: string): Promise<ApiResponse<{
    totalMinutes: number; sessionCount: number; noShowCount: number; byServiceType: Record<string, number>;
  }>> =>
    get(`/therapy/service-summary/${patientId}`, { params: { startDate, endDate } }),

  /** Import a transcript as a therapy note. Returns the saved note plus a
   * brief 2-3 sentence summary. No SOAP, no fabricated metrics — see
   * NOTES_AS_SOURCE_OF_TRUTH.md. */
  importTranscript: (data: {
    patientId: string;
    transcriptText?: string;
    fileContent?: string;
    fileType?: string;
    sessionDate?: string;
    patientName?: string;
    patientAge?: string;
    patientGoals?: { goalText: string }[];
  }): Promise<ApiResponse<{
    note: TherapyNote;
    summary: string;
  }>> =>
    post('/therapy/notes/import-transcript', data),

  /** Generate a therapy report */
  generateReport: (data: {
    patientId: string;
    reportType: string;
    dateRange: { start: string; end: string };
    outputFormat?: string;
    additionalData?: Record<string, unknown>;
  }): Promise<ApiResponse<{ jobId: string; statusUrl: string }>> =>
    post('/therapy/generate-report', data),
};

export const therapyGoals = {
  /** Create a therapy goal */
  create: (data: {
    patientId: string;
    goalText: string;
    goalArea: string;
    baseline?: string;
    targetAccuracy?: number;
    targetConsistency?: string;
    measurementMethod?: string;
    reportingDate?: string;
    conditions?: string;
  }): Promise<ApiResponse<TherapyGoal>> =>
    post('/therapy-goals', data),

  /** List goals for a patient */
  list: (patientId: string, status?: string): Promise<ApiResponse<TherapyGoal[]>> =>
    get(`/therapy-goals/${patientId}`, { params: { status } }),

  /** Get a specific goal */
  get: (patientId: string, goalId: string): Promise<ApiResponse<TherapyGoal>> =>
    get(`/therapy-goals/${patientId}/${goalId}`),

  /** Update a goal */
  update: (patientId: string, goalId: string, data: Partial<TherapyGoal>): Promise<ApiResponse<TherapyGoal>> =>
    put(`/therapy-goals/${patientId}/${goalId}`, data),

  /** Append progress data to a goal */
  addProgress: (patientId: string, goalId: string, data: {
    date?: string; accuracy?: number; cueingLevel?: string; trials?: number; notes?: string;
  }): Promise<ApiResponse<TherapyGoal>> =>
    post(`/therapy-goals/${patientId}/${goalId}/progress`, data),
};

// ============================================================================
// Intelligibility Endpoints
// ============================================================================

export interface IntelligibilityMark {
  wordIndex: number;
  utteranceIndex: number;
  intelligible: boolean;
}

export interface IntelligibilityData {
  evaluationId: string;
  intelligibilityMarks: IntelligibilityMark[];
  totalWords: number;
  wordsMarkedUnintelligible: number;
  rawScore: number;
  listenerFamiliarity: 'familiar' | 'unfamiliar';
  sampleType: 'connected_speech' | 'single_word';
  timestamp: string;
  attestedBy: string;
  flags?: string[];
  normativeContext?: Record<string, any>;
  severityClassification?: string;
}

export const intelligibility = {
  /** Save intelligibility marks for a session */
  save: (sessionId: string, data: {
    intelligibilityMarks: IntelligibilityMark[];
    totalWords: number;
    utteranceCount: number;
    listenerFamiliarity?: 'familiar' | 'unfamiliar';
    sampleType?: 'connected_speech' | 'single_word';
    ageMonths?: number;
  }): Promise<ApiResponse<IntelligibilityData>> =>
    put(`/sessions/${sessionId}/intelligibility`, data),

  /** Get intelligibility marks for a session */
  get: (sessionId: string): Promise<ApiResponse<IntelligibilityData | null>> =>
    get(`/sessions/${sessionId}/intelligibility`),
};

export default {
  patients,
  sessions,
  transcription,
  phonemeAnalysis,
  documents,
  reports,
  context,
  myslp,
  progressReports,
  goals,
  progressData,
  pdfExport,
  emailDelivery,
  subscription,
  groups,
  user,
  health,
  uploadSessionAudio,
  districtReports,
  scheduler,
  tts,
  agreements,
  templates,
  literacy,
  therapy,
  therapyGoals,
  intelligibility,
};
